/**
 * Testing & Safety Management Component
 * Admin interface for agent testing, evaluation, and safety guardrails
 */

import { useState, useEffect } from "react";
import { authFetch } from '../api/orchestrator';

// ==================== INTERFACES ====================

interface TestCase {
  question: string;
  expected: string;
  rubric?: string;
  context?: Record<string, any>;
  category?: string;
}

interface TestSet {
  id: string;
  tenant_id: string;
  chat_agent_id: string;
  name: string;
  description?: string;
  test_cases: TestCase[];
  pass_threshold: number;
  eval_method: "llm_judge" | "exact_match" | "semantic";
  is_active: boolean;
  tags?: string[];
}

interface EvalRun {
  id: string;
  test_set_id: string;
  agent_version_id: string;
  results: {
    pass_rate: number;
    total: number;
    passed: number;
    failed: number;
    failures: Array<{
      test_case: TestCase;
      actual_response: string;
      reason: string;
      score: number;
    }>;
  };
  status: "running" | "completed" | "failed";
  duration_ms?: number;
  started_at: string;
  completed_at?: string;
  can_promote: boolean;
}

interface SafetyPolicy {
  id: string;
  tenant_id: string;
  name: string;
  policy_type: "pii_detection" | "blocked_topics" | "tone_check" | "hallucination_check";
  rules: Record<string, any>;
  on_violation: "block" | "flag" | "modify" | "log_only";
  severity: "low" | "medium" | "high" | "critical";
  is_active: boolean;
  description?: string;
}

interface SafetyViolation {
  id: string;
  tenant_id: string;
  entity_id?: string;
  chat_id?: string;
  violation_type: string;
  severity: string;
  user_message: string;
  agent_response: string;
  detected_pattern: string;
  policy_rule: string;
  action_taken: string;
  modified_response?: string;
  created_at: string;
  reviewed: boolean;
}

interface SafetyStats {
  total_violations: number;
  by_type: Record<string, number>;
  by_severity: Record<string, number>;
  by_action: Record<string, number>;
  reviewed_count: number;
  recent_trend: Array<{ date: string; count: number }>;
}

// ==================== COMPONENT ====================

export default function TestingAndSafety() {
  const [activeTab, setActiveTab] = useState<"test-sets" | "evaluations" | "safety-policies" | "safety-violations">("test-sets");

  // Test Sets state
  const [testSets, setTestSets] = useState<TestSet[]>([]);
  const [selectedTestSet, setSelectedTestSet] = useState<TestSet | null>(null);

  // Evaluations state
  const [evalHistory, setEvalHistory] = useState<EvalRun[]>([]);
  const [runningEvals, setRunningEvals] = useState<Set<string>>(new Set());

  // Safety state
  const [safetyPolicies, setSafetyPolicies] = useState<SafetyPolicy[]>([]);
  const [safetyViolations, setSafetyViolations] = useState<SafetyViolation[]>([]);
  const [safetyStats, setSafetyStats] = useState<SafetyStats | null>(null);

  // Form state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTestSetName, setNewTestSetName] = useState("");
  const [newTestSetDesc, setNewTestSetDesc] = useState("");
  const [newTestCases, setNewTestCases] = useState("");
  const [newPassThreshold, setNewPassThreshold] = useState(80);

  useEffect(() => {
    loadTestSets();
    loadSafetyPolicies();
    loadSafetyViolations();
    loadSafetyStats();
  }, []);

  const loadTestSets = async () => {
    try {
      // Multi-tenant SaaS: Backend extracts tenant_id from JWT token
      const response = await authFetch("/api/agent-testing/test-sets");
      const data = await response.json();
      if (data.ok) {
        setTestSets(data.test_sets || []);
      }
    } catch (error) {
      console.error("Failed to load test sets:", error);
    }
  };

  const loadEvalHistory = async (testSetId: string) => {
    try {
      const response = await authFetch(`/api/agent-testing/test-sets/${testSetId}/history`);
      const data = await response.json();
      if (data.ok) {
        setEvalHistory(data.history || []);
      }
    } catch (error) {
      console.error("Failed to load eval history:", error);
    }
  };

  const loadSafetyPolicies = async () => {
    try {
      // Multi-tenant SaaS: Backend extracts tenant_id from JWT token
      const response = await authFetch("/api/safety/policies");
      const data = await response.json();
      if (data.ok) {
        setSafetyPolicies(data.policies || []);
      }
    } catch (error) {
      console.error("Failed to load safety policies:", error);
    }
  };

  const loadSafetyViolations = async () => {
    try {
      // Multi-tenant SaaS: Backend extracts tenant_id from JWT token
      const response = await authFetch("/api/safety/violations?limit=50");
      const data = await response.json();
      if (data.ok) {
        setSafetyViolations(data.violations || []);
      }
    } catch (error) {
      console.error("Failed to load safety violations:", error);
    }
  };

  const loadSafetyStats = async () => {
    try {
      // Multi-tenant SaaS: Backend extracts tenant_id from JWT token
      const response = await authFetch("/api/safety/stats?days=30");
      const data = await response.json();
      if (data.ok) {
        setSafetyStats(data.stats);
      }
    } catch (error) {
      console.error("Failed to load safety stats:", error);
    }
  };

  const handleCreateTestSet = async () => {
    try {
      const testCases: TestCase[] = newTestCases.split("\n").map((line) => {
        const parts = line.split("|");
        return {
          question: parts[0]?.trim() || "",
          expected: parts[1]?.trim() || "",
          rubric: parts[2]?.trim(),
        };
      }).filter((tc) => tc.question && tc.expected);

      const response = await authFetch("/api/agent-testing/test-sets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // Multi-tenant SaaS: tenant_id extracted from JWT by backend
          chat_agent_id: "default", // Would be selected from UI
          name: newTestSetName,
          description: newTestSetDesc,
          test_cases: testCases,
          pass_threshold: newPassThreshold / 100,
          eval_method: "llm_judge",
        }),
      });

      const data = await response.json();
      if (data.ok) {
        alert("Test set created successfully!");
        setShowCreateModal(false);
        setNewTestSetName("");
        setNewTestSetDesc("");
        setNewTestCases("");
        setNewPassThreshold(80);
        loadTestSets();
      } else {
        alert("Failed to create test set: " + (data.error || "Unknown error"));
      }
    } catch (error) {
      alert("Error creating test set: " + error);
    }
  };

  const handleRunEvaluation = async (testSetId: string) => {
    // In production, would select agent version from UI
    const agentVersionId = "default-agent-version";

    try {
      const response = await authFetch(`/api/agent-testing/test-sets/${testSetId}/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ agent_version_id: agentVersionId }),
      });

      const data = await response.json();
      if (data.ok) {
        setRunningEvals((prev) => new Set(prev).add(testSetId));
        alert("Evaluation started! Check back shortly for results.");
        // Poll for results
        setTimeout(() => {
          setRunningEvals((prev) => {
            const next = new Set(prev);
            next.delete(testSetId);
            return next;
          });
          loadEvalHistory(testSetId);
        }, 5000);
      } else {
        alert("Failed to start evaluation: " + (data.error || "Unknown error"));
      }
    } catch (error) {
      alert("Error starting evaluation: " + error);
    }
  };

  const togglePolicy = async (policyId: string, isActive: boolean) => {
    try {
      const response = await authFetch(`/api/safety/policies/${policyId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: isActive }),
      });

      const data = await response.json();
      if (data.ok) {
        loadSafetyPolicies();
      } else {
        alert("Failed to update policy: " + (data.error || "Unknown error"));
      }
    } catch (error) {
      alert("Error updating policy: " + error);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical": return "#dc3545";
      case "high": return "#fd7e14";
      case "medium": return "#ffc107";
      case "low": return "#28a745";
      default: return "#6c757d";
    }
  };

  return (
    <div className="testing-safety-management">
      <h2>✅ Testing & Safety</h2>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: "20px", borderBottom: "1px solid #dee2e6" }}>
        <button
          className={`tab-button ${activeTab === "test-sets" ? "active" : ""}`}
          onClick={() => setActiveTab("test-sets")}
          style={{
            padding: "10px 20px",
            background: "none",
            border: "none",
            borderBottom: activeTab === "test-sets" ? "2px solid #3b82f6" : "none",
            cursor: "pointer",
            fontWeight: activeTab === "test-sets" ? "bold" : "normal",
          }}
        >
          📋 Test Sets
        </button>
        <button
          className={`tab-button ${activeTab === "evaluations" ? "active" : ""}`}
          onClick={() => setActiveTab("evaluations")}
          style={{
            padding: "10px 20px",
            background: "none",
            border: "none",
            borderBottom: activeTab === "evaluations" ? "2px solid #3b82f6" : "none",
            cursor: "pointer",
            fontWeight: activeTab === "evaluations" ? "bold" : "normal",
          }}
        >
          📊 Evaluations
        </button>
        <button
          className={`tab-button ${activeTab === "safety-policies" ? "active" : ""}`}
          onClick={() => setActiveTab("safety-policies")}
          style={{
            padding: "10px 20px",
            background: "none",
            border: "none",
            borderBottom: activeTab === "safety-policies" ? "2px solid #3b82f6" : "none",
            cursor: "pointer",
            fontWeight: activeTab === "safety-policies" ? "bold" : "normal",
          }}
        >
          🛡️ Safety Policies
        </button>
        <button
          className={`tab-button ${activeTab === "safety-violations" ? "active" : ""}`}
          onClick={() => setActiveTab("safety-violations")}
          style={{
            padding: "10px 20px",
            background: "none",
            border: "none",
            borderBottom: activeTab === "safety-violations" ? "2px solid #3b82f6" : "none",
            cursor: "pointer",
            fontWeight: activeTab === "safety-violations" ? "bold" : "normal",
          }}
        >
          ⚠️ Violations
        </button>
      </div>

      {/* Test Sets Tab */}
      {activeTab === "test-sets" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h3>Test Sets</h3>
            <button
              onClick={() => setShowCreateModal(true)}
              style={{ padding: "8px 16px", background: "#3b82f6", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
            >
              + Create Test Set
            </button>
          </div>

          {testSets.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#6c757d" }}>
              No test sets found. Create one to get started!
            </div>
          ) : (
            <div className="test-sets-list">
              {testSets.map((testSet) => (
                <div
                  key={testSet.id}
                  style={{
                    border: "1px solid #dee2e6",
                    borderRadius: "8px",
                    padding: "16px",
                    marginBottom: "12px",
                    background: "#fff",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <h4 style={{ margin: "0 0 8px 0" }}>{testSet.name}</h4>
                      <p style={{ margin: "0 0 8px 0", color: "#6c757d", fontSize: "14px" }}>
                        {testSet.description || "No description"}
                      </p>
                      <div style={{ fontSize: "12px", color: "#6c757d" }}>
                        <span>Test Cases: {testSet.test_cases.length}</span>
                        {" | "}
                        <span>Pass Threshold: {(testSet.pass_threshold * 100).toFixed(0)}%</span>
                        {" | "}
                        <span>Method: {testSet.eval_method}</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => {
                          setSelectedTestSet(testSet);
                          loadEvalHistory(testSet.id);
                          setActiveTab("evaluations");
                        }}
                        style={{ padding: "6px 12px", background: "#6c757d", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleRunEvaluation(testSet.id)}
                        disabled={runningEvals.has(testSet.id)}
                        style={{
                          padding: "6px 12px",
                          background: runningEvals.has(testSet.id) ? "#6c757d" : "#28a745",
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                          cursor: runningEvals.has(testSet.id) ? "not-allowed" : "pointer",
                        }}
                      >
                        {runningEvals.has(testSet.id) ? "Running..." : "Run Eval"}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Evaluations Tab */}
      {activeTab === "evaluations" && (
        <div>
          <h3>Evaluation Results</h3>

          {selectedTestSet && (
            <div style={{ marginBottom: "20px", padding: "12px", background: "#f8f9fa", borderRadius: "4px" }}>
              <strong>Test Set:</strong> {selectedTestSet.name}
            </div>
          )}

          {evalHistory.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#6c757d" }}>
              No evaluation runs found. Run an evaluation to see results.
            </div>
          ) : (
            <div className="eval-history">
              {evalHistory.map((evalRun) => (
                <div
                  key={evalRun.id}
                  style={{
                    border: "1px solid #dee2e6",
                    borderRadius: "8px",
                    padding: "16px",
                    marginBottom: "12px",
                    background: evalRun.can_promote ? "#f8fff9" : "#fff8f8",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <h4 style={{ margin: "0 0 8px 0" }}>
                        {new Date(evalRun.started_at).toLocaleString()}
                      </h4>
                      <div style={{ display: "flex", gap: "16px", fontSize: "14px" }}>
                        <span>Pass Rate: {(evalRun.results.pass_rate * 100).toFixed(1)}%</span>
                        <span>Passed: {evalRun.results.passed} / {evalRun.results.total}</span>
                        <span>Failed: {evalRun.results.failed}</span>
                        {evalRun.duration_ms && <span>Duration: {(evalRun.duration_ms / 1000).toFixed(1)}s</span>}
                      </div>
                    </div>
                    <div>
                      {evalRun.can_promote ? (
                        <span style={{ padding: "4px 8px", background: "#28a745", color: "white", borderRadius: "4px", fontSize: "12px" }}>
                          ✓ Ready to Promote
                        </span>
                      ) : (
                        <span style={{ padding: "4px 8px", background: "#dc3545", color: "white", borderRadius: "4px", fontSize: "12px" }}>
                          ✗ Below Threshold
                        </span>
                      )}
                    </div>
                  </div>

                  {evalRun.results.failures.length > 0 && (
                    <details style={{ marginTop: "12px" }}>
                      <summary style={{ cursor: "pointer", color: "#dc3545" }}>
                        View {evalRun.results.failures.length} Failed Tests
                      </summary>
                      <div style={{ marginTop: "8px", paddingLeft: "16px" }}>
                        {evalRun.results.failures.map((failure, idx) => (
                          <div key={idx} style={{ marginBottom: "8px", padding: "8px", background: "#fff8f8", borderRadius: "4px" }}>
                            <div><strong>Q:</strong> {failure.test_case.question}</div>
                            <div><strong>Expected:</strong> {failure.test_case.expected}</div>
                            <div><strong>Actual:</strong> {failure.actual_response}</div>
                            <div style={{ color: "#dc3545" }}><strong>Reason:</strong> {failure.reason}</div>
                            <div><strong>Score:</strong> {(failure.score * 100).toFixed(1)}%</div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Safety Policies Tab */}
      {activeTab === "safety-policies" && (
        <div>
          <h3>Safety Policies</h3>

          {/* Safety Stats */}
          {safetyStats && (
            <div className="stats-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "24px" }}>
              <div className="stat-card" style={{ padding: "16px", background: "#f8f9fa", borderRadius: "8px", border: "1px solid #dee2e6" }}>
                <div style={{ fontSize: "24px", fontWeight: "bold", color: "#dc3545" }}>{safetyStats.total_violations}</div>
                <div style={{ fontSize: "14px", color: "#6c757d" }}>Total Violations (30d)</div>
              </div>
              <div className="stat-card" style={{ padding: "16px", background: "#f8f9fa", borderRadius: "8px", border: "1px solid #dee2e6" }}>
                <div style={{ fontSize: "24px", fontWeight: "bold", color: "#ffc107" }}>{safetyStats.by_action?.blocked || 0}</div>
                <div style={{ fontSize: "14px", color: "#6c757d" }}>Blocked</div>
              </div>
              <div className="stat-card" style={{ padding: "16px", background: "#f8f9fa", borderRadius: "8px", border: "1px solid #dee2e6" }}>
                <div style={{ fontSize: "24px", fontWeight: "bold", color: "#28a745" }}>{safetyStats.reviewed_count}</div>
                <div style={{ fontSize: "14px", color: "#6c757d" }}>Reviewed</div>
              </div>
            </div>
          )}

          <div className="policies-list">
            {safetyPolicies.map((policy) => (
              <div
                key={policy.id}
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "12px",
                  background: policy.is_active ? "#fff" : "#f8f9fa",
                  opacity: policy.is_active ? 1 : 0.6,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                      <h4 style={{ margin: 0 }}>{policy.name}</h4>
                      <span style={{
                        padding: "2px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        background: getSeverityColor(policy.severity),
                        color: "white",
                      }}>
                        {policy.severity}
                      </span>
                      {!policy.is_active && (
                        <span style={{ padding: "2px 8px", borderRadius: "4px", fontSize: "12px", background: "#6c757d", color: "white" }}>
                          Inactive
                        </span>
                      )}
                    </div>
                    <p style={{ margin: "0 0 8px 0", color: "#6c757d", fontSize: "14px" }}>
                      {policy.description || "No description"}
                    </p>
                    <div style={{ fontSize: "12px", color: "#6c757d" }}>
                      <span>Type: {policy.policy_type}</span>
                      {" | "}
                      <span>Action: {policy.on_violation}</span>
                    </div>
                  </div>
                  <div>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input
                        type="checkbox"
                        checked={policy.is_active}
                        onChange={(e) => togglePolicy(policy.id, e.target.checked)}
                      />
                      Active
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Safety Violations Tab */}
      {activeTab === "safety-violations" && (
        <div>
          <h3>Safety Violations</h3>

          {safetyViolations.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#6c757d" }}>
              No safety violations recorded. Good job!
            </div>
          ) : (
            <div className="violations-list">
              {safetyViolations.map((violation) => (
                <div
                  key={violation.id}
                  style={{
                    border: "1px solid #dee2e6",
                    borderRadius: "8px",
                    padding: "16px",
                    marginBottom: "12px",
                    background: "#fff",
                    borderLeft: `4px solid ${getSeverityColor(violation.severity)}`,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                        <span style={{
                          padding: "2px 8px",
                          borderRadius: "4px",
                          fontSize: "12px",
                          background: getSeverityColor(violation.severity),
                          color: "white",
                        }}>
                          {violation.severity}
                        </span>
                        <span style={{ fontSize: "12px", color: "#6c757d" }}>
                          {violation.violation_type.replace(/_/g, " ")}
                        </span>
                        <span style={{ fontSize: "12px", color: "#6c757d" }}>
                          {new Date(violation.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div style={{ fontSize: "12px", color: "#6c757d" }}>
                        Action: {violation.action_taken.replace(/_/g, " ")}
                        {violation.reviewed && " ✓ Reviewed"}
                      </div>
                    </div>
                  </div>

                  <div style={{ background: "#f8f9fa", padding: "8px", borderRadius: "4px", fontSize: "13px" }}>
                    <div><strong>User:</strong> {violation.user_message}</div>
                    <div><strong>Agent:</strong> {violation.agent_response}</div>
                    <div style={{ color: "#dc3545" }}><strong>Detected:</strong> {violation.detected_pattern}</div>
                    {violation.modified_response && (
                      <div style={{ color: "#28a745" }}><strong>Modified:</strong> {violation.modified_response}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Test Set Modal */}
      {showCreateModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}>
          <div style={{ background: "#fff", borderRadius: "8px", padding: "24px", maxWidth: "600px", width: "100%" }}>
            <h3 style={{ marginTop: 0 }}>Create Test Set</h3>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "4px", fontWeight: "bold" }}>Name *</label>
              <input
                type="text"
                value={newTestSetName}
                onChange={(e) => setNewTestSetName(e.target.value)}
                style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                placeholder="e.g., EP Eligibility Tests"
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "4px", fontWeight: "bold" }}>Description</label>
              <input
                type="text"
                value={newTestSetDesc}
                onChange={(e) => setNewTestSetDesc(e.target.value)}
                style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
                placeholder="Brief description of what this test set covers"
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "4px", fontWeight: "bold" }}>Pass Threshold (%)</label>
              <input
                type="number"
                min="0"
                max="100"
                value={newPassThreshold}
                onChange={(e) => setNewPassThreshold(parseInt(e.target.value))}
                style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px" }}
              />
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", marginBottom: "4px", fontWeight: "bold" }}>Test Cases (one per line: Question | Expected | Rubric)</label>
              <textarea
                value={newTestCases}
                onChange={(e) => setNewTestCases(e.target.value)}
                style={{ width: "100%", padding: "8px", border: "1px solid #ccc", borderRadius: "4px", minHeight: "120px", fontFamily: "monospace" }}
                placeholder="What is the minimum salary for EP? | The minimum salary for Employment Pass is $9,000 | Check exact salary amount&#10;Can I apply for PR with EP? | Yes, EP holders can apply for PR after 6 months | Check eligibility criteria"
              />
            </div>

            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{ padding: "8px 16px", background: "#6c757d", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTestSet}
                disabled={!newTestSetName || !newTestCases}
                style={{
                  padding: "8px 16px",
                  background: (!newTestSetName || !newTestCases) ? "#6c757d" : "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: (!newTestSetName || !newTestCases) ? "not-allowed" : "pointer",
                }}
              >
                Create Test Set
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
